import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  BookOpen, Save, Send, ChevronLeft, ChevronRight, 
  CheckCircle, Circle, Upload, FileText, AlertCircle
} from 'lucide-react';
import RichTextEditor from '../components/RichTextEditor';

const QualitativeAssessmentPage = () => {
  const { assessmentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [questions, setQuestions] = useState([]);
  const [responses, setResponses] = useState({});
  const [currentPillar, setCurrentPillar] = useState(1);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [phase, setPhase] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const organizationId = location.state?.organizationId || localStorage.getItem('organization_id') || 'b8b52a08-165e-4a21-85ae-099e7268a29e';

  useEffect(() => {
    fetchQuestions();
    fetchPhase();
    fetchExistingResponses();
  }, []);

  const fetchQuestions = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/assessments/questions`);
      const data = await response.json();
      setQuestions(data.filter(q => q.is_active));
    } catch (error) {
      console.error('Error fetching questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPhase = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/assessment-phases/${assessmentId}/${organizationId}`);
      const data = await response.json();
      setPhase(data);
    } catch (error) {
      console.error('Error fetching phase:', error);
    }
  };

  const fetchExistingResponses = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/org-responses/${assessmentId}/${organizationId}?phase=qualitative`);
      const data = await response.json();
      
      const responsesMap = {};
      data.forEach(r => {
        responsesMap[r.question_id] = {
          response_text: r.response_text,
          status: r.status,
          response_id: r.response_id
        };
      });
      setResponses(responsesMap);
    } catch (error) {
      console.error('Error fetching responses:', error);
    }
  };

  const pillarQuestions = questions.filter(q => q.pillar_number === currentPillar);
  const currentQuestion = pillarQuestions[currentQuestionIndex];

  const handleSaveResponse = async (status = 'draft') => {
    if (!currentQuestion) return;

    setSaving(true);
    try {
      const responseData = {
        assessment_id: assessmentId,
        organization_id: organizationId,
        question_id: currentQuestion.question_id,
        phase: 'qualitative',
        pillar_number: currentQuestion.pillar_number,
        question_number: currentQuestion.question_number,
        response_text: responses[currentQuestion.question_id]?.response_text || '',
        status: status,
        submitted_by_user_id: organizationId
      };

      const response = await fetch(`${BACKEND_URL}/api/org-responses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(responseData)
      });

      if (response.ok) {
        const result = await response.json();
        setResponses(prev => ({
          ...prev,
          [currentQuestion.question_id]: {
            ...prev[currentQuestion.question_id],
            response_id: result.response_id,
            status: status
          }
        }));
        
        if (status === 'submitted') {
          // Move to next question
          if (currentQuestionIndex < pillarQuestions.length - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
          } else if (currentPillar < 8) {
            setCurrentPillar(currentPillar + 1);
            setCurrentQuestionIndex(0);
          }
        }
        
        fetchPhase(); // Refresh phase data
      }
    } catch (error) {
      console.error('Error saving response:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleResponseChange = (value) => {
    setResponses(prev => ({
      ...prev,
      [currentQuestion.question_id]: {
        ...prev[currentQuestion.question_id],
        response_text: value
      }
    }));
  };

  const getResponseStatus = (questionId) => {
    return responses[questionId]?.status || 'not_started';
  };

  const getCompletionStats = () => {
    const submitted = Object.values(responses).filter(r => r.status === 'submitted').length;
    return {
      submitted,
      total: 80,
      percentage: Math.round((submitted / 80) * 100)
    };
  };

  const stats = getCompletionStats();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading assessment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white sticky top-0 z-10 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/org-admin/assessments')}
                className="p-2 hover:bg-white/20 rounded-lg transition-all"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-2xl font-black">Qualitative Assessment</h1>
                <p className="text-blue-100 text-sm">Complete all 60 questions to proceed to payment</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-blue-100">Progress</p>
              <p className="text-2xl font-bold">{stats.submitted}/80</p>
              <div className="w-32 bg-white/20 rounded-full h-2 mt-1">
                <div
                  className="bg-white h-2 rounded-full transition-all"
                  style={{ width: `${stats.percentage}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-12 gap-6">
          {/* Sidebar - Pillar Navigation */}
          <div className="col-span-3">
            <div className="bg-white rounded-xl shadow-lg p-6 sticky top-24">
              <h3 className="font-bold text-gray-900 mb-4">Pillars</h3>
              <div className="space-y-2">
                {[1, 2, 3, 4, 5, 6, 7, 8].map(pillar => {
                  const pillarQs = questions.filter(q => q.pillar_number === pillar);
                  const completed = pillarQs.filter(q => getResponseStatus(q.question_id) === 'submitted').length;
                  
                  return (
                    <button
                      key={pillar}
                      onClick={() => {
                        setCurrentPillar(pillar);
                        setCurrentQuestionIndex(0);
                      }}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
                        currentPillar === pillar
                          ? 'bg-blue-600 text-white shadow-lg'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-semibold">Pillar {pillar}</span>
                        <span className="text-xs">{completed}/10</span>
                      </div>
                      {completed === 10 && (
                        <CheckCircle className="w-4 h-4 text-green-500 absolute right-2 top-3" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Main Question Area */}
          <div className="col-span-9">
            {currentQuestion ? (
              <div className="bg-white rounded-xl shadow-xl overflow-hidden">
                {/* Question Header */}
                <div className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="bg-white/20 px-3 py-1 rounded-lg text-sm font-bold">
                      {currentQuestion.question_id}
                    </span>
                    <span className={`px-3 py-1 rounded-lg text-xs font-semibold ${
                      getResponseStatus(currentQuestion.question_id) === 'submitted'
                        ? 'bg-green-500 text-white'
                        : 'bg-white/20'
                    }`}>
                      {getResponseStatus(currentQuestion.question_id) === 'submitted' ? 'Submitted' : 'Draft'}
                    </span>
                  </div>
                  <h2 className="text-xl font-bold mb-1">{currentQuestion.pillar_name}</h2>
                  <p className="text-indigo-100 text-sm">Question {currentQuestionIndex + 1} of {pillarQuestions.length}</p>
                </div>

                {/* Question Content */}
                <div className="p-8">
                  <div className="mb-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4" dangerouslySetInnerHTML={{ __html: currentQuestion.question_text }}></h3>
                    {currentQuestion.help_text && (
                      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
                        <p className="text-sm text-blue-800" dangerouslySetInnerHTML={{ __html: currentQuestion.help_text }}></p>
                      </div>
                    )}
                  </div>

                  {/* Response Editor */}
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Your Response</label>
                    <RichTextEditor
                      value={responses[currentQuestion.question_id]?.response_text || ''}
                      onChange={handleResponseChange}
                      placeholder="Enter your detailed response here..."
                    />
                  </div>

                  {/* File Upload (if required) */}
                  {currentQuestion.file_upload_required && (
                    <div className="mb-6">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        <Upload className="w-4 h-4 inline mr-2" />
                        Supporting Documents
                      </label>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                        <FileText className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">Drag and drop files or click to browse</p>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                    <button
                      onClick={() => {
                        if (currentQuestionIndex > 0) {
                          setCurrentQuestionIndex(currentQuestionIndex - 1);
                        } else if (currentPillar > 1) {
                          setCurrentPillar(currentPillar - 1);
                          setCurrentQuestionIndex(9);
                        }
                      }}
                      disabled={currentPillar === 1 && currentQuestionIndex === 0}
                      className="flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-5 h-5" />
                      Previous
                    </button>

                    <div className="flex gap-3">
                      <button
                        onClick={() => handleSaveResponse('draft')}
                        disabled={saving}
                        className="flex items-center gap-2 px-6 py-3 bg-blue-100 text-blue-700 rounded-lg font-semibold hover:bg-blue-200 transition-all disabled:opacity-50"
                      >
                        <Save className="w-5 h-5" />
                        {saving ? 'Saving...' : 'Save Draft'}
                      </button>

                      <button
                        onClick={() => handleSaveResponse('submitted')}
                        disabled={saving || !responses[currentQuestion.question_id]?.response_text}
                        className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Send className="w-5 h-5" />
                        {saving ? 'Submitting...' : 'Submit & Next'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Question Navigation Footer */}
                <div className="bg-gray-50 px-8 py-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-600">
                      Pillar {currentPillar} - Question {currentQuestionIndex + 1} of {pillarQuestions.length}
                    </p>
                    <div className="flex gap-1">
                      {pillarQuestions.map((q, idx) => (
                        <button
                          key={q.question_id}
                          onClick={() => setCurrentQuestionIndex(idx)}
                          className={`w-8 h-8 rounded flex items-center justify-center text-xs font-bold transition-all ${
                            idx === currentQuestionIndex
                              ? 'bg-blue-600 text-white'
                              : getResponseStatus(q.question_id) === 'submitted'
                              ? 'bg-green-500 text-white'
                              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                          }`}
                        >
                          {idx + 1}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No questions available for this pillar</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QualitativeAssessmentPage;
